<template>
  <b-container ref="container" fluid class="bg-white shadow position-relative px-3 pb-5 px-6-lg pb-2-lg w-lg-70">
    <div class="full-mask" v-if="mfsAdmin && !startedByAdmin && !adminEditing"></div>
    <div class="py-3 above-full-mask">
      <h2 >General Details</h2>
      <router-link tag="a" :to="`/notification/${notification.id}`">
        Notification #{{notification.fonNumber}}
      </router-link>
    </div>
    <b-form class="pb-lg-0 pb-md-8 pb-0">
      <ValidationObserver ref="observer" slim>
        <b-card>
          <ValidationProvider
            name="Harvest"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <div role="group" class="form-row form-group justify-content-between">
              <label id="harvestLabel" for="harvest" class="col-lg-8 col-12 col-form-label">
                <span class="req-ind">*</span>Was this property harvested during calendar year
                {{landownerReport.reportingYear}}?
              </label>
              <b-form-radio-group
                id="harvest"
                name="harvest"
                v-model="generalDetails.harvest"
                aria-labelledby="harvestLabel"
                :aria-describedby="$errorsIdString(errors, 'harvest')"
              >
                <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
                <b-form-radio class="noselect" :value="false">No</b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="harvest"
                class="d-block invalid-feedback text-right"
              />
            </div>
          </ValidationProvider>
          <template>
            <ValidationProvider
              name="Harvest Complete"
              rules="required"
              v-slot="{ errors }"
              slim
            >
              <div role="group" class="form-row form-group justify-content-between">
                <label id="harvestCompleteLabel" for="harvestComplete" class="col-lg-8 col-12 col-form-label">
                  <span class="req-ind">*</span>Was the harvest completed during
                  {{landownerReport.reportingYear}}?
                  <FontAwesomeIcon
                    focusable="true"
                    tabindex="0"
                    class="mx-2 text-primary align-self-center"
                    icon="info-circle"
                    size="lg"
                    id="harvestCompletePopover"
                  />
                  <b-popover
                    target="harvestCompletePopover"
                    :triggers="['hover', 'focus']"
                    placement="right"
                  >
                    If you have completed your timber harvest and do not plan to harvest any
                    additional acreage under this notification, check "Yes," otherwise check "No."
                  </b-popover>
                </label>
                <b-form-radio-group
                  id="harvestComplete"
                  name="harvestComplete"
                  v-model="generalDetails.harvestComplete"
                  aria-labelledby="harvestCompleteLabel"
                  :aria-describedby="$errorsIdString(errors, 'harvestComplete')"
                >
                  <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
                  <b-form-radio class="noselect" :value="false">No</b-form-radio>
                </b-form-radio-group>
                <InputErrors
                  :errors="errors"
                  errorsIdPrefix="harvestComplete"
                  class="d-block invalid-feedback text-right"
                />
              </div>
            </ValidationProvider>
            <ValidationProvider v-if="generalDetails.harvest"
              name="Method of Sale or Sale Type"
              rules="required"
              v-slot="{ errors }"
            >
              <div role="group" class="form-group">
                <label id="saleTypeLabel" for="saleType" >
                  <span class="req-ind">*</span>Method of Payment or Type of Sale
                </label>
                <b-form-radio-group
                  id="saleType"
                  name="saleType"
                  v-model="generalDetails.saleTypeId"
                  aria-labelledby="saleTypeLabel"
                  :aria-describedby="$errorsIdString(errors, 'saleType')"
                >
                  <div v-for="option in saleTypes" :key="option.id" class="row no-gutters">
                    <div class="col-11 col-lg-auto">
                      <b-form-radio :value="option.id">
                        {{option.name}}
                      </b-form-radio>
                    </div>
                    <div class="col-1 col-lg-auto" v-if="option.description">
                      <FontAwesomeIcon
                        focusable="true"
                        tabindex="0"
                        class="mx-2 text-primary align-self-center"
                        icon="info-circle"
                        :id="`sale-type-popover-${option.id}`"
                      />
                      <b-popover
                        :target="`sale-type-popover-${option.id}`"
                        :triggers="['hover', 'focus']"
                        placement="right"
                      >
                        {{option.description}}
                      </b-popover>
                    </div>
                  </div>
                </b-form-radio-group>
                <InputErrors
                  :errors="errors"
                  errorsIdPrefix="saleType"
                  class="d-block invalid-feedback"
                />
              </div>
            </ValidationProvider>
          </template>
          <ValidationProvider
            name="Non-harvest"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <div role="group" class="form-row form-group justify-content-between">
              <label id="nonHarvestLabel" for="nonHarvest" class="col-lg-8 col-12 col-form-label">
                <span class="req-ind">*</span>Did you perform any non-harvest activities on this
                property including herbicide, non-commercial thinning and/or tree planting during
                calendar year {{landownerReport.reportingYear}}?
              </label>
              <b-form-radio-group
                id="nonHarvest"
                name="nonHarvest"
                v-model="generalDetails.nonHarvest"
                aria-labelledby="nonHarvestLabel"
                :aria-describedby="$errorsIdString(errors, 'nonHarvest')"
              >
                <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
                <b-form-radio class="noselect" :value="false">No</b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="nonHarvest"
                class="d-block invalid-feedback text-right"
              />
            </div>
          </ValidationProvider>
        </b-card>
      </ValidationObserver>
    </b-form>
    <b-card v-if="adminEditing" class="mt-3">
      <b-form-group label="Editor's Comment" label-for="editorCommentGeneralDetails">
        <b-form-textarea
          id="editorCommentGeneralDetails"
          v-model="editorComment"
          placeholder="Add a comment about changes made to General Details..."
          rows="3"
          max-rows="6"
          maxlength="255"
        />
      </b-form-group>
    </b-card>
  </b-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import LookupModules from '@/store/moduleConfig';
import FormSubmit from '@/mixins/FormSubmit';
import Utilities from '@/mixins/Utilities';

export default {
  name: 'GeneralDetailsForm',
  mixins: [FormSubmit, Utilities],
  props: ['landownerReport'],
  data() {
    let generalDetails = {
      harvest: null,
      nonHarvest: null,
      harvestComplete: null,
      saleTypeId: null,
    };
    generalDetails = this.mergeTargetProps(generalDetails, this.landownerReport);
    return {
      generalDetails,
    };
  },
  mounted() {
    this.$nextTick(() => {
      if (this.mfsAdmin && !this.startedByAdmin && !this.adminEditing) {
        if (this.disableTabbing) {
          this.disableTabbing(this.$refs.container);
        }
      }
    });
  },
  created() {
    this.$store.dispatch(`${LookupModules.SALE_TYPES.namespace}/fetchOptions`);
  },
  watch: {
    landownerReport() {
      this.generalDetails = this.mergeTargetProps(this.generalDetails, this.landownerReport);
    },
  },
  computed: {
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
    ...mapState(LookupModules.SALE_TYPES.namespace, {
      saleTypes: state => state.options,
    }),
    ...mapGetters('User', ['mfsAdmin']),
    ...mapGetters('LandownerReport', ['adminEditing']),
    editorComment: {
      get() {
        return this.$store.state.LandownerReport.editorComments.generalDetails;
      },
      set(value) {
        this.$store.dispatch('LandownerReport/setEditorComment', { section: 'generalDetails', comment: value });
      },
    },
    startedByAdmin() {
      return this.landownerReport.statusId === 3;
    },
  },
  methods: {
    async submitWrapper() {
      const action = this.adminEditing
        ? 'LandownerReport/adminUpdateGeneralDetails'
        : 'LandownerReport/updateGeneralDetails';
      return this.submit(
        this.$refs.observer,
        action,
        this.generalDetails,
      );
    },
  },
};
</script>
